import { subDays, subQuarters, subYears } from 'date-fns';
import { snapStartDateToCalendarBoundary } from 'pages/Dashboard/DeploymentAndValueDashboard/components/time_util';
import { RadioSelectQuickFilterButton } from 'shared/filter_pane/RadioSelectQuickFilterButton';

export type DashboardTimeRangePreset = (typeof DashboardTimeRangePresets)[number];

export const DashboardTimeRangePresets = [
    'last-7-days',
    'last-30-days',
    'last-90-days',
    'last-365-days',
    'last-quarter',
    'last-year',
    'last-2-years',
    'all-time',
] as const;

export type TimeRangeFilterButtonProps = {
    selectedPreset: DashboardTimeRangePreset | undefined;
    onChangePreset: (preset: DashboardTimeRangePreset) => void;
};

const TIME_RANGE_OPTIONS: Array<{ value: DashboardTimeRangePreset; label: string }> = [
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'last-365-days', label: 'Last 365 Days' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'last-2-years', label: 'Last 2 Years' },
    { value: 'all-time', label: 'All Time' },
];

/**
 * Converts a time range preset to start/end dates
 */
export function presetToDateRange(preset: DashboardTimeRangePreset): {
    startDateTime: Date;
    endDateTime: Date | undefined;
} {
    const referenceDate = new Date();
    let startDateTime: Date;

    switch (preset) {
        case 'last-7-days':
            startDateTime = subDays(referenceDate, 7);
            break;
        case 'last-30-days':
            startDateTime = subDays(referenceDate, 30);
            break;
        case 'last-90-days':
            startDateTime = subDays(referenceDate, 90);
            break;
        case 'last-365-days':
            startDateTime = subDays(referenceDate, 365);
            break;
        case 'last-quarter':
            startDateTime = snapStartDateToCalendarBoundary(
                subQuarters(referenceDate, 1),
                90,
            );
            break;
        case 'last-year':
            startDateTime = snapStartDateToCalendarBoundary(
                subYears(referenceDate, 1),
                365,
            );
            break;
        case 'last-2-years':
            startDateTime = snapStartDateToCalendarBoundary(
                subYears(referenceDate, 2),
                730,
            );
            break;
        case 'all-time':
            // 20 years ago as a reasonable "all time" limit
            startDateTime = snapStartDateToCalendarBoundary(
                subYears(referenceDate, 20),
                365 * 20,
            );
            break;
    }

    return {
        startDateTime,
        endDateTime: undefined, // undefined means "today"
    };
}

/**
 * Attempts to match current date range to a preset.
 * Returns the matching preset or undefined if no exact match.
 */
export function dateRangeToPreset(
    startDateTime: Date,
    endDateTime: Date | undefined,
): DashboardTimeRangePreset | undefined {
    // Only try to match if endDateTime is undefined (meaning "today")
    if (endDateTime !== undefined) {
        return undefined;
    }

    // Try each preset and see if it produces the same start date
    for (const option of TIME_RANGE_OPTIONS) {
        const presetRange = presetToDateRange(option.value);
        // Compare dates by time value
        if (presetRange.startDateTime.getTime() === startDateTime.getTime()) {
            return option.value;
        }
    }

    return undefined;
}

export function TimeRangeFilterButton({
    selectedPreset,
    onChangePreset,
}: TimeRangeFilterButtonProps) {
    return (
        <RadioSelectQuickFilterButton
            id="time-range-filter"
            label="Time Range"
            selectedValue={selectedPreset}
            onChangeValue={onChangePreset}
            options={TIME_RANGE_OPTIONS}
        />
    );
}
